/**
 * Saved Cards (Bookmarks) API - PocketBase version
 * Replaces src/api/savedCards.js for PocketBase backend
 */

import pb from '../pocketbase';

/**
 * Проверяет, сохранена ли карточка пользователем
 */
export async function isCardSaved(cardId) {
    try {
        if (!pb.authStore.isValid) return false;

        const records = await pb.collection('bookmarks').getList(1, 1, {
            filter: `user = '${pb.authStore.model.id}' && card = '${cardId}'`,
        });

        return records.totalItems > 0;
    } catch (error) {
        console.error('Error checking if card is saved:', error);
        return false;
    }
}

/**
 * Сохраняет карточку для пользователя
 */
export async function saveCard(cardId) {
    try {
        if (!pb.authStore.isValid) {
            throw new Error('User not authenticated');
        }

        // Check if already saved
        const existing = await isCardSaved(cardId);
        if (existing) return true;

        await pb.collection('bookmarks').create({
            user: pb.authStore.model.id,
            card: cardId,
        });

        return true;
    } catch (error) {
        console.error('Error saving card:', error);
        return false;
    }
}

/**
 * Удаляет карточку из сохраненных
 */
export async function unsaveCard(cardId) {
    try {
        if (!pb.authStore.isValid) {
            throw new Error('User not authenticated');
        }

        // Find the bookmark
        const records = await pb.collection('bookmarks').getList(1, 1, {
            filter: `user = '${pb.authStore.model.id}' && card = '${cardId}'`,
        });

        if (records.items.length > 0) {
            await pb.collection('bookmarks').delete(records.items[0].id);
        }

        return true;
    } catch (error) {
        console.error('Error unsaving card:', error);
        return false;
    }
}

/**
 * Проверяет массив карточек на наличие сохраненных
 */
export async function checkMultipleSavedCards(cardIds) {
    try {
        if (!cardIds || cardIds.length === 0 || !pb.authStore.isValid) {
            return new Set();
        }

        const cardIdsStr = cardIds.map(id => `'${id}'`).join(',');

        const records = await pb.collection('bookmarks').getFullList({
            filter: `user = '${pb.authStore.model.id}' && card ?~ ${cardIdsStr}`,
        });

        return new Set(records.map(r => r.card));
    } catch (error) {
        console.error('Error in checkMultipleSavedCards:', error);
        return new Set();
    }
}

/**
 * Получает список всех сохраненных карточек пользователя
 */
export async function getSavedCards(from = 0, to = 99) {
    try {
        if (!pb.authStore.isValid) return [];

        const page = Math.floor(from / (to - from + 1)) + 1;
        const perPage = to - from + 1;

        const records = await pb.collection('bookmarks').getList(page, perPage, {
            filter: `user = '${pb.authStore.model.id}'`,
            sort: '-created',
            expand: 'card,card.game',
        });

        return records.items.map(bookmark => ({
            saved_at: bookmark.created,
            card: bookmark.expand?.card || null,
        }));
    } catch (error) {
        console.error('Error in getSavedCards:', error);
        return [];
    }
}

// Helper for formatting card URLs
function formatCardUrl(card, gameId, type = 'full') {
    if (!card) return null;

    // If card has image field, use PocketBase file URL
    if (card.image) {
        const baseUrl = pb.baseURL;
        const thumb = type === 'thumb' ? '?thumb=200x200' : '';
        return `${baseUrl}/api/files/cards/${card.id}/${card.image}${thumb}`;
    }

    // Fallback to legacy path format
    const cardNumber = String(card.idx).padStart(2, '0');
    const folder = type === 'thumb' ? 'webp-thumbs' : 'webp';
    return `${pb.baseURL}/api/files/games/${gameId}/${folder}/${cardNumber}.webp`;
}

/**
 * Получает список игр с количеством сохраненных карточек
 */
export async function getSavedCardsByGames() {
    try {
        if (!pb.authStore.isValid) throw new Error('User not authenticated');

        const records = await pb.collection('bookmarks').getFullList({
            filter: `user = '${pb.authStore.model.id}'`,
            expand: 'card.game',
        });

        const gamesMap = new Map();

        records.forEach(bookmark => {
            const card = bookmark.expand?.card;
            const game = card?.expand?.game;

            if (!game) return;

            if (!gamesMap.has(game.id)) {
                gamesMap.set(game.id, {
                    id: game.id,
                    collectionName: 'games', // Required for getFileUrl
                    title: game.title,
                    subtitle: game.subtitle,
                    cover: game.cover,
                    cover_thumb: game.cover_thumb,
                    saved_count: 0
                });
            }
            gamesMap.get(game.id).saved_count++;
        });

        return Array.from(gamesMap.values())
            .sort((a, b) => b.saved_count - a.saved_count);
    } catch (error) {
        console.error('Error in getSavedCardsByGames:', error);
        return [];
    }
}

/**
 * Получает все сохраненные карточки с полной информацией
 */
export async function getAllSavedCardsWithDetails() {
    try {
        if (!pb.authStore.isValid) throw new Error('User not authenticated');

        const records = await pb.collection('bookmarks').getFullList({
            filter: `user = '${pb.authStore.model.id}'`,
            sort: '-created',
            expand: 'card,card.game',
        });

        return records.map(bookmark => {
            const card = bookmark.expand?.card;
            const game = card?.expand?.game;

            return {
                id: card?.id,
                gameId: game?.id,
                idx: card?.idx || 0,
                question1: card?.question || '',
                question2: card?.subquestion || '',
                cardPicUrl: formatCardUrl(card, game?.id, 'full'),
                cardThumbUrl: formatCardUrl(card, game?.id, 'thumb'),
                saved_at: bookmark.created
            };
        });
    } catch (error) {
        console.error('Error in getAllSavedCardsWithDetails:', error);
        return [];
    }
}

/**
 * Получает сохраненные карточки для конкретной игры
 */
export async function getSavedCardsByGame(gameId) {
    try {
        if (!pb.authStore.isValid) throw new Error('User not authenticated');

        const records = await pb.collection('bookmarks').getFullList({
            filter: `user = '${pb.authStore.model.id}'`,
            sort: '-created',
            expand: 'card,card.game',
        });

        return records
            .filter(bookmark => bookmark.expand?.card?.game === gameId)
            .map(bookmark => {
                const card = bookmark.expand?.card;

                return {
                    id: card?.id,
                    gameId: gameId,
                    idx: card?.idx || 0,
                    question1: card?.question || '',
                    question2: card?.subquestion || '',
                    cardPicUrl: formatCardUrl(card, gameId, 'full'),
                    cardThumbUrl: formatCardUrl(card, gameId, 'thumb'),
                    saved_at: bookmark.created
                };
            });
    } catch (error) {
        console.error('Error in getSavedCardsByGame:', error);
        return [];
    }
}

export default {
    isCardSaved,
    saveCard,
    unsaveCard,
    checkMultipleSavedCards,
    getSavedCards,
    getSavedCardsByGames,
    getAllSavedCardsWithDetails,
    getSavedCardsByGame,
};
